import { motion } from 'framer-motion'

interface CircularProgressProps {
  value: number // 0-100
  size?: 'sm' | 'md' | 'lg' | 'xl'
  strokeWidth?: number
  showValue?: boolean
  label?: string
  color?: string
  className?: string
}

const sizeMap = {
  sm: { width: 80, fontSize: 'text-lg' },
  md: { width: 120, fontSize: 'text-2xl' },
  lg: { width: 160, fontSize: 'text-3xl' },
  xl: { width: 200, fontSize: 'text-4xl' },
}

function getColorFromScore(score: number): string {
  if (score >= 75) return '#22C55E' // green
  if (score >= 50) return '#46C0C6' // teal (brand color)
  if (score >= 25) return '#F59E0B' // amber
  return '#EF4444' // red
}

export function CircularProgress({
  value,
  size = 'md',
  strokeWidth = 8,
  showValue = true,
  label,
  color,
  className = '',
}: CircularProgressProps) {
  const { width, fontSize } = sizeMap[size]
  const radius = (width - strokeWidth) / 2
  const circumference = radius * 2 * Math.PI
  const offset = circumference - (value / 100) * circumference
  const strokeColor = color || getColorFromScore(value)

  return (
    <div className={`relative inline-flex flex-col items-center ${className}`}>
      <svg
        width={width}
        height={width}
        className="transform -rotate-90"
      >
        {/* Background circle */}
        <circle
          cx={width / 2}
          cy={width / 2}
          r={radius}
          fill="none"
          stroke="#E5E7EB"
          strokeWidth={strokeWidth}
        />
        {/* Progress circle */}
        <motion.circle
          cx={width / 2}
          cy={width / 2}
          r={radius}
          fill="none"
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1.5, ease: 'easeOut' }}
          style={{
            strokeDasharray: circumference,
          }}
        />
      </svg>
      {showValue && (
        <motion.div
          className="absolute inset-0 flex flex-col items-center justify-center"
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5, duration: 0.5 }}
        >
          <span className={`font-bold ${fontSize}`} style={{ color: strokeColor }}>
            {Math.round(value)}
          </span>
          {label && (
            <span className="text-xs text-gray-500 mt-1">{label}</span>
          )}
        </motion.div>
      )}
    </div>
  )
}
